import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Filter, X, Plus, Calendar, Link } from 'lucide-react';

const API_URL = (window.ENV?.REACT_APP_BACKEND_URL || window.location.origin) + '/api';

// Event type colors matching desktop aesthetics
const EVENT_TYPES = {
  shipping: { label: 'Shipping', color: '#3B82F6', icon: '🚚' },
  delivery: { label: 'Delivery', color: '#22C55E', icon: '📦' },
  install: { label: 'Install', color: '#EF4444', icon: '🔧' },
  order: { label: 'Order', color: '#F59E0B', icon: '🛒' },
  restock: { label: 'Restock', color: '#8B5CF6', icon: '📋' },
  project: { label: 'Project', color: '#D4A574', icon: '🏠' },
  google: { label: 'Google Calendar', color: '#4285F4', icon: '📅' },
  outlook: { label: 'Outlook Calendar', color: '#0078D4', icon: '📆' },
  todo: { label: 'To-Do', color: '#10B981', icon: '✓' },
};

// Shimmer CSS
const shimmerStyle = `
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
  .shimmer-gold {
    position: relative;
    overflow: hidden;
  }
  .shimmer-gold::after {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: linear-gradient(90deg, transparent 0%, rgba(255,215,0,0.1) 50%, transparent 100%);
    background-size: 200% 100%;
    animation: shimmer 3s infinite;
    pointer-events: none;
  }
`;

const ProjectCalendar = ({ onEventClick, compact = false, ordersOnly = false, theme = 'dark' }) => {
  const light = theme === 'light';
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFilters, setSelectedFilters] = useState(ordersOnly ? ['order', 'delivery'] : Object.keys(EVENT_TYPES));
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [hoveredEvent, setHoveredEvent] = useState(null);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    date: '',
    type: ordersOnly ? 'order' : 'project',
    description: '',
    project_id: ''
  });
  const [projects, setProjects] = useState([]);
  const [calendarConnections, setCalendarConnections] = useState([]);
  const [showConnectMenu, setShowConnectMenu] = useState(false);
  const [connectingCalendar, setConnectingCalendar] = useState(null);

  // Check URL for connection status
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const connected = urlParams.get('connected');
    const error = urlParams.get('error');
    
    if (connected) {
      alert(`✅ ${connected === 'google' ? 'Google Calendar' : 'Outlook Calendar'} connected successfully!`);
      window.history.replaceState({}, document.title, window.location.pathname);
      fetchCalendarConnections();
    }
    if (error) {
      alert(`❌ Connection failed: ${error}`);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Fetch calendar connections
  const fetchCalendarConnections = async () => {
    try {
      const response = await fetch(`${API_URL}/calendar-connections`);
      const connections = await response.json();
      setCalendarConnections(connections);
    } catch (error) {
      console.error('Failed to fetch calendar connections:', error);
    }
  };

  // Fetch all projects and extract dates
  useEffect(() => {
    fetchCalendarData();
    fetchCalendarConnections();
  }, []);

  const fetchCalendarData = async () => {
    try {
      setLoading(true);
      // Lightweight: project list for the dropdown (summary, NOT the full tree).
      let projectsData = [];
      try {
        const response = await fetch(`${API_URL}/projects/summary`);
        projectsData = await response.json();
        if (!Array.isArray(projectsData)) projectsData = [];
      } catch (e) { projectsData = []; }
      setProjects(projectsData); // Store projects for dropdown

      const calendarEvents = [];

      // ONE server-side aggregate of every date event (project dates, item
      // order/ship/delivery/install/restock dates, to-do deadlines). Replaces the
      // old per-project full-tree cascade that downloaded ~100MB and froze the
      // browser on the real studio database.
      try {
        const aggResp = await fetch(`${API_URL}/calendar/project-events`);
        const agg = await aggResp.json();
        (agg.events || []).forEach(ev => {
          calendarEvents.push({ ...ev, date: new Date(ev.date) });
        });
      } catch (e) {
        console.log('Project calendar aggregate skipped:', e.message || e);
      }
      
      // Also fetch independent calendar events
      try {
        const eventsResponse = await fetch(`${API_URL}/calendar-events`);
        const independentEvents = await eventsResponse.json();
        if (Array.isArray(independentEvents)) {
          independentEvents.forEach(evt => {
            calendarEvents.push({
              id: evt.id,
              type: evt.type || 'project',
              title: evt.title,
              date: new Date(evt.date),
              project: evt.project_name || 'Independent',
              projectId: evt.project_id,
              description: evt.description,
              isIndependent: true
            });
          });
        }
      } catch (e) {
        console.log('No independent events endpoint');
      }
      
      // Fetch external calendar events (Google & Outlook) with timeout
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const externalResponse = await fetch(`${API_URL}/external-calendar-events`, {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        
        const externalEvents = await externalResponse.json();
        console.log('External calendar events loaded:', externalEvents?.length || 0);
        if (Array.isArray(externalEvents)) {
          externalEvents.forEach(evt => {
            calendarEvents.push({
              id: evt.id,
              type: evt.type || evt.source, // 'google' or 'outlook'
              title: evt.title,
              date: new Date(evt.date),
              endDate: evt.end_date ? new Date(evt.end_date) : null,
              project: evt.calendar_name || 'External Calendar',
              description: evt.description,
              location: evt.location,
              isExternal: true,
              source: evt.source,
              calendarEmail: evt.calendar_email
            });
          });
        }
      } catch (e) {
        console.log('External calendar events skipped:', e.message || e);
      }
      
      // Fetch To-Do items with deadlines from all projects
      // (now included in the /calendar/project-events aggregate above)

      setEvents(calendarEvents);
    } catch (error) {
      console.error('Failed to fetch calendar data:', error);
    } finally {
      setLoading(false);
    }
  };

  // In orders-only mode (the project Master Calendar) the calendar surfaces
  // ONLY "order placed" and "order delivered" events — no shipping, install,
  // restock, project, to-do or external-calendar noise in between.
  const VISIBLE_EVENT_TYPES = ordersOnly
    ? { order: EVENT_TYPES.order, delivery: EVENT_TYPES.delivery }
    : EVENT_TYPES;

  // Filter events based on selected filters
  const filteredEvents = useMemo(() => {
    return events.filter(event =>
      selectedFilters.includes(event.type) &&
      (!ordersOnly || event.type === 'order' || event.type === 'delivery')
    );
  }, [events, selectedFilters, ordersOnly]);

  // Get calendar grid data
  const calendarData = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(year, month, 0);
    for (let i = startDay - 1; i >= 0; i--) {
      days.push({
        date: new Date(year, month - 1, prevMonth.getDate() - i),
        isCurrentMonth: false,
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const dayEvents = filteredEvents.filter(event => {
        const eventDate = new Date(event.date);
        return eventDate.getFullYear() === year &&
               eventDate.getMonth() === month &&
               eventDate.getDate() === i;
      });
      days.push({
        date,
        isCurrentMonth: true,
        events: dayEvents,
      });
    }
    
    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false,
      });
    }
    
    return days;
  }, [currentDate, filteredEvents]);

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const toggleFilter = (type) => {
    setSelectedFilters(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const selectAllFilters = () => setSelectedFilters(Object.keys(VISIBLE_EVENT_TYPES));
  const clearAllFilters = () => setSelectedFilters([]);

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                      'July', 'August', 'September', 'October', 'November', 'December'];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  // Count events by type for legend
  const eventCounts = useMemo(() => {
    const counts = {};
    Object.keys(VISIBLE_EVENT_TYPES).forEach(type => {
      counts[type] = events.filter(e => e.type === type).length;
    });
    return counts;
  }, [events]);

  // Save independent calendar event
  const saveCalendarEvent = async () => {
    if (!newEvent.title || !newEvent.date) {
      alert('Please enter a title and date');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/calendar-events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: newEvent.title,
          date: newEvent.date,
          type: newEvent.type,
          description: newEvent.description,
          project_id: newEvent.project_id || null,
          project_name: projects.find(p => p.id === newEvent.project_id)?.name || 'Independent'
        })
      });
      
      if (response.ok) {
        setShowAddEvent(false);
        setNewEvent({ title: '', date: '', type: ordersOnly ? 'order' : 'project', description: '', project_id: '' });
        fetchCalendarData(); // Refresh
      } else {
        alert('Failed to save event');
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Failed to save event');
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg p-8 text-center" style={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #3a3a3a 50%, #2a2a2a 100%)',
        border: '1px solid #8b7355',
      }}>
        <div className="text-stone-300 text-lg">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="rounded-lg overflow-hidden" style={{
      background: light ? '#FFFFFF' : 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)',
      border: light ? '1px solid rgba(139,115,85,0.45)' : '1px solid #8b7355',
      boxShadow: light ? '0 2px 12px rgba(60,45,25,0.10)' : '0 4px 15px rgba(139, 115, 85, 0.2)',
    }}>
      <style>{shimmerStyle}</style>
      
      {/* Header */}
      <div className="p-4 shimmer-gold" style={{
        background: 'linear-gradient(135deg, #8b7355 0%, #a0845c 50%, #8b7355 100%)',
      }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <h2 className="text-2xl font-bold text-white">📅 Project Calendar</h2>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm rounded transition-all hover:scale-105"
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              Today
            </button>
            <button
              onClick={() => setShowAddEvent(true)}
              className="px-3 py-1 text-sm rounded transition-all hover:scale-105 font-semibold"
              style={{
                background: '#22c55e',
                color: 'white',
                border: '1px solid #16a34a',
              }}
            >
              + Add Event
            </button>
          </div>
          
          {/* Month Navigation */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigateMonth(-1)}
              className="p-2 rounded-full hover:bg-black/20 transition-colors text-white"
            >
              <ChevronLeft size={24} />
            </button>
            <span className="text-xl font-semibold text-white min-w-[200px] text-center">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </span>
            <button
              onClick={() => navigateMonth(1)}
              className="p-2 rounded-full hover:bg-black/20 transition-colors text-white"
            >
              <ChevronRight size={24} />
            </button>
          </div>
          
          {/* Filter Button */}
          <div className="relative">
            <button
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded transition-all hover:scale-105"
              style={{
                background: 'rgba(0,0,0,0.3)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              <Filter size={18} />
              Filter ({selectedFilters.length}/{Object.keys(VISIBLE_EVENT_TYPES).length})
            </button>
            
            {/* Filter Dropdown */}
            {showFilterMenu && (
              <div className="absolute right-0 top-full mt-2 z-50 rounded-lg shadow-xl p-4 min-w-[250px]"
                style={{
                  background: '#2a2a2a',
                  border: '1px solid #8b7355',
                }}>
                <div className="flex justify-between items-center mb-3 pb-2" style={{ borderBottom: '1px solid #8b7355' }}>
                  <span className="font-semibold text-stone-200">Filter Events</span>
                  <button onClick={() => setShowFilterMenu(false)} className="text-stone-400 hover:text-white">
                    <X size={18} />
                  </button>
                </div>
                
                <div className="flex gap-2 mb-3">
                  <button onClick={selectAllFilters} className="text-xs px-2 py-1 rounded bg-green-600/30 text-green-400 hover:bg-green-600/50">
                    Select All
                  </button>
                  <button onClick={clearAllFilters} className="text-xs px-2 py-1 rounded bg-red-600/30 text-red-400 hover:bg-red-600/50">
                    Clear All
                  </button>
                </div>
                
                <div className="space-y-2">
                  {Object.entries(VISIBLE_EVENT_TYPES).map(([type, config]) => (
                    <label key={type} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-white/5">
                      <input
                        type="checkbox"
                        checked={selectedFilters.includes(type)}
                        onChange={() => toggleFilter(type)}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: config.color }}
                      />
                      <span className="text-lg">{config.icon}</span>
                      <span className="text-stone-200 flex-1">{config.label}</span>
                      <span className="text-xs px-2 py-0.5 rounded" style={{ background: config.color + '30', color: config.color }}>
                        {eventCounts[type]}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {/* Add Calendar Button */}
          <div className="relative" style={{ zIndex: 100 }}>
            <button
              onClick={() => setShowConnectMenu(!showConnectMenu)}
              className="flex items-center gap-2 px-4 py-2 rounded transition-all hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, #4285F4 0%, #0078D4 100%)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
              }}
            >
              <Plus size={18} />
              Add Calendar
            </button>
            
            {/* Connect Calendar Dropdown */}
            {showConnectMenu && (
              <div className="fixed right-4 top-auto mt-2 rounded-lg shadow-2xl p-4 min-w-[320px]"
                style={{
                  background: '#1a1a1a',
                  border: '2px solid #d4af37',
                  zIndex: 9999,
                  maxHeight: '80vh',
                  overflowY: 'auto',
                }}>
                <div className="flex justify-between items-center mb-3 pb-2" style={{ borderBottom: '1px solid #8b7355' }}>
                  <span className="font-semibold text-stone-200">Connect Calendar</span>
                  <button onClick={() => setShowConnectMenu(false)} className="text-stone-400 hover:text-white">
                    <X size={18} />
                  </button>
                </div>
                
                {/* Connected Calendars */}
                {calendarConnections.length > 0 && (
                  <div className="mb-4">
                    <div className="text-xs text-stone-400 mb-2">Connected Calendars:</div>
                    {calendarConnections.map(conn => (
                      <div key={conn.id} className="flex items-center justify-between p-2 rounded mb-1" 
                        style={{ background: 'rgba(255,255,255,0.05)' }}>
                        <div className="flex items-center gap-2">
                          <span>{conn.provider === 'google' ? '📅' : '📆'}</span>
                          <div>
                            <div className="text-sm text-stone-200">{conn.name}</div>
                            <div className="text-xs text-stone-400">{conn.email}</div>
                          </div>
                        </div>
                        <button
                          onClick={async () => {
                            if (confirm('Disconnect this calendar?')) {
                              await fetch(`${API_URL}/calendar-connections/${conn.id}`, { method: 'DELETE' });
                              fetchCalendarConnections();
                              fetchCalendarData();
                            }
                          }}
                          className="text-red-400 hover:text-red-300 text-xs px-2 py-1"
                        >
                          Disconnect
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Calendar */}
                <div className="text-xs text-stone-400 mb-2">Add New Calendar:</div>
                <div className="space-y-2">
                  <button
                    onClick={async () => {
                      setConnectingCalendar('google');
                      try {
                        const response = await fetch(`${API_URL}/auth/google/login`);
                        const data = await response.json();
                        if (data.authorization_url) {
                          window.location.href = data.authorization_url;
                          return;
                        }
                        alert(data.message || 'Could not start Google sign-in.');
                      } catch (err) {
                        console.error('Google connect failed:', err);
                        alert('Could not reach the server to start Google sign-in.');
                      } finally {
                        setConnectingCalendar(null);
                      }
                    }}
                    disabled={connectingCalendar === 'google'}
                    className="w-full flex items-center gap-3 p-3 rounded transition-all hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(135deg, #4285F4 0%, #34A853 100%)',
                      color: 'white',
                    }}
                  >
                    <span className="text-xl">📅</span>
                    <div className="text-left">
                      <div className="font-semibold">Google Calendar</div>
                      <div className="text-xs opacity-80">Connect your Google Calendar</div>
                    </div>
                  </button>
                  
                  <button
                    onClick={async () => {
                      setConnectingCalendar('outlook');
                      try {
                        const response = await fetch(`${API_URL}/auth/outlook/login`);
                        const data = await response.json();
                        if (data.authorization_url) {
                          window.location.href = data.authorization_url;
                          return;
                        }
                        // Microsoft sign-in isn't set up. Say so, rather than
                        // leaving the button stuck on "connecting" forever.
                        alert(data.message || 'Microsoft sign-in is not set up yet.');
                      } catch (err) {
                        console.error('Outlook connect failed:', err);
                        alert('Could not reach the server to start Microsoft sign-in.');
                      } finally {
                        setConnectingCalendar(null);
                      }
                    }}
                    disabled={connectingCalendar === 'outlook'}
                    className="w-full flex items-center gap-3 p-3 rounded transition-all hover:scale-[1.02]"
                    style={{
                      background: 'linear-gradient(135deg, #0078D4 0%, #00BCF2 100%)',
                      color: 'white',
                    }}
                  >
                    <span className="text-xl">📆</span>
                    <div className="text-left">
                      <div className="font-semibold">Outlook Calendar</div>
                      <div className="text-xs opacity-80">Connect your Microsoft Outlook</div>
                    </div>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Calendar Grid */}
      <div className="p-4">
        {/* Day Headers */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map(day => (
            <div key={day} className="text-center text-sm font-semibold py-2" style={{ color: light ? '#9A7636' : '#d4af37' }}>
              {day}
            </div>
          ))}
        </div>
        
        {/* Calendar Days */}
        <div className="grid grid-cols-7 gap-1">
          {calendarData.map((day, index) => (
            <div
              key={index}
              onClick={() => day.events?.length > 0 && setSelectedDay(day)}
              className={`min-h-[100px] p-1 rounded-lg transition-all cursor-pointer ${
                day.isCurrentMonth ? 'hover:bg-white/5' : 'opacity-40'
              } ${isToday(day.date) ? 'ring-2 ring-[#d4af37]' : ''}`}
              style={{
                background: day.isCurrentMonth ? (light ? '#F7F4EE' : 'rgba(255,255,255,0.02)') : 'transparent',
                border: light ? '1px solid rgba(139, 115, 85, 0.28)' : '1px solid rgba(139, 115, 85, 0.2)',
              }}
            >
              <div className="text-right text-sm mb-1" style={{ color: isToday(day.date) ? '#d4af37' : (light ? '#6B6255' : '#a8a29e'), fontWeight: isToday(day.date) ? 700 : 400 }}>
                {day.date.getDate()}
              </div>
              
              {/* Event Dots/Pills */}
              <div className="space-y-0.5">
                {day.events?.slice(0, 3).map((event, i) => (
                  <div
                    key={event.id}
                    onMouseEnter={() => setHoveredEvent(event)}
                    onMouseLeave={() => setHoveredEvent(null)}
                    className="text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:scale-105 transition-transform"
                    style={{
                      background: EVENT_TYPES[event.type].color + '40',
                      color: EVENT_TYPES[event.type].color,
                      border: `1px solid ${EVENT_TYPES[event.type].color}50`,
                    }}
                  >
                    {EVENT_TYPES[event.type].icon} {event.title.substring(0, 15)}...
                  </div>
                ))}
                {day.events?.length > 3 && (
                  <div className="text-[10px] text-center" style={{ color: light ? '#6B6255' : '#a8a29e' }}>
                    +{day.events.length - 3} more
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Legend */}
      <div className="px-4 pb-4">
        <div className="flex flex-wrap gap-4 justify-center p-3 rounded-lg" style={{
          background: light ? '#F4F1EA' : 'rgba(0,0,0,0.3)',
          border: '1px solid rgba(139, 115, 85, 0.3)',
        }}>
          {Object.entries(VISIBLE_EVENT_TYPES).map(([type, config]) => (
            <div key={type} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ background: config.color }}></div>
              <span className="text-sm" style={{ color: light ? '#3A332A' : '#d6d3d1' }}>{config.icon} {config.label}</span>
              <span className="text-xs" style={{ color: light ? '#6B6255' : '#78716c' }}>({eventCounts[type]})</span>
            </div>
          ))}
        </div>
      </div>
      
      {/* Selected Day Modal */}
      {selectedDay && selectedDay.events?.length > 0 && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={() => setSelectedDay(null)}>
          <div
            className="max-w-lg w-full max-h-[80vh] overflow-auto rounded-lg"
            onClick={e => e.stopPropagation()}
            style={{
              background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 50%, #1a1a1a 100%)',
              border: '1px solid #8b7355',
            }}
          >
            <div className="p-4 sticky top-0" style={{
              background: 'linear-gradient(135deg, #8b7355 0%, #a0845c 50%, #8b7355 100%)',
            }}>
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold text-white">
                  {selectedDay.date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </h3>
                <button onClick={() => setSelectedDay(null)} className="text-white hover:bg-black/20 p-1 rounded">
                  <X size={24} />
                </button>
              </div>
              <p className="text-white/70 text-sm">{selectedDay.events.length} event(s)</p>
            </div>
            
            <div className="p-4 space-y-3">
              {selectedDay.events.map(event => (
                <div
                  key={event.id}
                  className="p-4 rounded-lg cursor-pointer hover:scale-102 transition-transform"
                  style={{
                    background: EVENT_TYPES[event.type].color + '15',
                    border: `1px solid ${EVENT_TYPES[event.type].color}50`,
                  }}
                  onClick={() => onEventClick && onEventClick(event)}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{EVENT_TYPES[event.type].icon}</span>
                    <span className="font-bold text-stone-200">{event.title}</span>
                  </div>
                  <div className="space-y-1 text-sm text-stone-400">
                    <div>📁 Project: <span className="text-[#d4af37]">{event.project}</span></div>
                    {event.room && <div>🏠 Room: {event.room}</div>}
                    {event.vendor && <div>🏪 Vendor: {event.vendor}</div>}
                    {event.tracking && <div>📦 Tracking: {event.tracking}</div>}
                    <div className="mt-2 pt-2" style={{ borderTop: '1px solid rgba(139, 115, 85, 0.3)' }}>
                      <span className="px-2 py-1 rounded text-xs" style={{
                        background: EVENT_TYPES[event.type].color + '30',
                        color: EVENT_TYPES[event.type].color,
                      }}>
                        {EVENT_TYPES[event.type].label}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      
      {/* Hover Tooltip */}
      {hoveredEvent && (
        <div className="fixed z-50 p-3 rounded-lg shadow-xl max-w-xs pointer-events-none"
          style={{
            background: '#2a2a2a',
            border: `1px solid ${EVENT_TYPES[hoveredEvent.type].color}`,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}>
          <div className="font-bold text-stone-200">{hoveredEvent.title}</div>
          <div className="text-sm text-stone-400">{hoveredEvent.project}</div>
          <div className="text-xs mt-1" style={{ color: EVENT_TYPES[hoveredEvent.type].color }}>
            {EVENT_TYPES[hoveredEvent.type].icon} {EVENT_TYPES[hoveredEvent.type].label}
          </div>
        </div>
      )}

      {/* Add Event Modal */}
      {showAddEvent && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md" style={{ border: '1px solid #8b7355' }}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-[#d4af37]">Add Calendar Event</h3>
              <button onClick={() => setShowAddEvent(false)} className="text-stone-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-stone-400 mb-1">Event Title *</label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700 focus:border-[#d4af37] outline-none"
                  placeholder="Enter event title"
                />
              </div>
              
              <div>
                <label className="block text-sm text-stone-400 mb-1">Date *</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700 focus:border-[#d4af37] outline-none"
                />
              </div>
              
              <div>
                <label className="block text-sm text-stone-400 mb-1">Event Type</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700 focus:border-[#d4af37] outline-none"
                >
                  {Object.entries(VISIBLE_EVENT_TYPES).map(([key, { label, icon }]) => (
                    <option key={key} value={key}>{icon} {label}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-stone-400 mb-1">Link to Project (Optional)</label>
                <select
                  value={newEvent.project_id}
                  onChange={(e) => setNewEvent({ ...newEvent, project_id: e.target.value })}
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700 focus:border-[#d4af37] outline-none"
                >
                  <option value="">-- Independent Event --</option>
                  {projects.map(project => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-stone-400 mb-1">Description (Optional)</label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  className="w-full px-3 py-2 rounded bg-gray-800 text-white border border-gray-700 focus:border-[#d4af37] outline-none"
                  rows={3}
                  placeholder="Enter description"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAddEvent(false)}
                  className="flex-1 px-4 py-2 rounded bg-gray-700 text-white hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={saveCalendarEvent}
                  className="flex-1 px-4 py-2 rounded font-semibold transition-colors"
                  style={{ background: '#d4af37', color: 'black' }}
                >
                  Save Event
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectCalendar;
